// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportSnackbar } from '@/components/support-snackbar';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { Project } from '@/project/api/types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { SparseLocation } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  defaultLocation: z
    .object({ id: z.string(), name: z.string() })
    .nullable()
    .transform((value) => value as SparseLocation | null),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  project: Project;
  onClose: () => void;
}

export function ProjectDefaultLocationDialog(props: Props) {
  const updateProjectMutation = useUpdateProject(props.project.key, {
    onSuccess: () => {
      props.onClose();
    },
  });

  const { control, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { defaultLocation: props.project.defaultLocation },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateProjectMutation.mutate({
      defaultLocationId: data.defaultLocation?.id ?? null,
    });
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);

    window.open(url, '_blank');
  };

  const close = () => {
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateProjectMutation.isError}
        message="An error occurred while updating the project."
        onClose={updateProjectMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Edit default location</DialogTitle>
        <DialogContent dividers>
          <FormLocationSearch
            name="defaultLocation"
            label="Default location"
            grouping={['customer']}
            priorityCustomerId={props.project.customer?.id}
            control={control}
            disabled={updateProjectMutation.isPending}
            onCreate={handleCreateLocation}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={close}
            disabled={updateProjectMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateProjectMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
